import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { Spinner } from '@/components/common/Spinner';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import usePartner from '@/hooks/usePartner';
import { PartnerDashboardDetailsProps } from '@/models/PartnerDashboard';
import { ProgramNames } from '@/models/Program';
import { ApplicationStatus, ApplicationStatusesText } from '@/utils/constants';
import useApplication from '@/hooks/useApplication';
import { ApplicationDataGridRowProps } from '@/models/Application';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';

export default function PartnerDeleteModal({
  modalPartner,
  partnerDeleteModalOpen,
  setPartnerDeleteModalOpen,
  selectedProgramId,
}: {
  modalPartner: PartnerDashboardDetailsProps;
  partnerDeleteModalOpen: boolean;
  setPartnerDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedProgramId: ProgramNames;
}) {
  const { deletePartner, getPartnerGroup } = usePartner();

  const { getAppsForApplicantDashboard } = useApplication();
  const [partnerGroupName, setPartnerGroupName] = useState<string>('');
  const [numApplications, setNumApplications] = useState<number>(0);
  const [partnerAppsLoading, setPartnerAppsLoading] = useState<boolean>(false);
  const [modalPartnerApps, setModalPartnerApps] = useState<string[]>([]);
  const [modalPartnerHistoricApps, setModalPartnerHistoricApps] = useState<string[]>([]);

  const [postingPartner, setPostingPartner] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { partnersAll, updatePartnersAll } = useContext(SelectedPartnerContext);
  const { setAlert } = useContext(SnackbarContext);

  const formatApplicationType = (type: string) =>
    type.replace('Tree City', 'TC').replace('Tree Cities', 'TC').replace('-Specific', '');

  const updatePartners = useCallback(
    (pid: number) => {
      const pa = { ...partnersAll };
      let ps = [...pa[selectedProgramId]];

      ps = ps.filter((x) => x.id != pid);
      pa[selectedProgramId] = ps;

      updatePartnersAll(pa);
    },
    [partnersAll, selectedProgramId, updatePartnersAll]
  );

  const postPartnerDelete = async () => {
    setShowWarning(false);
    if (modalPartner.id != null) {
      setPostingPartner(true);

      try {
        await deletePartner(modalPartner.id);
        updatePartners(modalPartner.id);
        setPartnerDeleteModalOpen(false);
        setPostingPartner(false);
      } catch (error: unknown) {
        setAlert({
          severity: 'error',
          message: `Failed to delete partner. ${(error as Error).message}`,
        });
        setPostingPartner(false);
        throw error;
      }
    }
  };

  const getPartnerCurrentApps = useCallback(
    (apps: ApplicationDataGridRowProps[]) => {
      const selectedProgramApps = apps.filter(
        (a: ApplicationDataGridRowProps) => a.programId == selectedProgramId
      );

      const currentApps = selectedProgramApps
        .filter((a: ApplicationDataGridRowProps) => a.applicationCycleActive)
        .sort((a: ApplicationDataGridRowProps, b: ApplicationDataGridRowProps) => {
          const aTypeId = a.applicationTypeId ?? Number.MAX_SAFE_INTEGER;
          const bTypeId = b.applicationTypeId ?? Number.MAX_SAFE_INTEGER;

          if (aTypeId !== bTypeId) return aTypeId - bTypeId;
          return (a.applicationId ?? 0) - (b.applicationId ?? 0);
        })
        .map((a: ApplicationDataGridRowProps) => {
          const appType = formatApplicationType(a.applicationType ?? 'Unknown Type');
          const statusDescription = ApplicationStatusesText[a.status];
          const baseApp = `${a.applicationId} (${appType}, ${statusDescription}`;

          return a.status == ApplicationStatus.InProgress
            ? `${baseApp}, ${(a.applicationCompletionRate * 100).toFixed(0)}%)`
            : `${baseApp})`;
        });

      const currentAppsOutput = currentApps.length > 0 ? currentApps : ['None'];

      const historicAppsFrequency = new Map<string, Map<string, number>>();

      selectedProgramApps
        .filter((a: ApplicationDataGridRowProps) => !a.applicationCycleActive)
        .forEach((a: ApplicationDataGridRowProps) => {
          const appType = formatApplicationType(a.applicationType ?? 'Unknown Type');
          const statusDescription = ApplicationStatusesText[a.status];

          const statusCounts = historicAppsFrequency.get(appType) ?? new Map<string, number>();
          const existingCount = statusCounts.get(statusDescription) ?? 0;
          statusCounts.set(statusDescription, existingCount + 1);
          historicAppsFrequency.set(appType, statusCounts);
        });

      const historicApps: string[] = Array.from(historicAppsFrequency.entries())
        .sort(([appTypeA], [appTypeB]) => appTypeA.localeCompare(appTypeB))
        .flatMap(([appType, statusCounts]) => {
          return Array.from(statusCounts.entries())
            .sort(([statusA], [statusB]) => statusA.localeCompare(statusB))
            .map(([statusDescription, count]) => `${appType}, ${statusDescription} (${count})`);
        });

      const historicAppsOutput = historicApps.length > 0 ? historicApps : ['None'];

      return { current: currentAppsOutput, historic: historicAppsOutput };
    },
    [selectedProgramId]
  );

  const handleOpenConfirmModal = () => {
    setConfirmDeleteOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setConfirmDeleteOpen(false);
  };

  useEffect(() => {
    const loadData = async () => {
      if (modalPartner != null) {
        setPartnerAppsLoading(true);

        setPartnerGroupName('');
        setModalPartnerApps([]);
        setModalPartnerHistoricApps([]);

        if (modalPartner?.partnerGroup?.id != null) {
          const r = await getPartnerGroup(modalPartner.partnerGroup.id);
          setPartnerGroupName(`${r?.partnerName} (${r?.id})`);
        }

        if (modalPartner.id != null) {
          const r = await getAppsForApplicantDashboard(modalPartner.id);
          const apps = getPartnerCurrentApps(r);
          setModalPartnerApps(apps['current']);
          setModalPartnerHistoricApps(apps['historic']);

          setNumApplications(r.length);
          setPartnerAppsLoading(false);
        }
      }
    };

    void loadData();
  }, [getPartnerGroup, getAppsForApplicantDashboard, getPartnerCurrentApps, modalPartner]);

  const handleClose = () => {
    setShowWarning(false);
    setPartnerDeleteModalOpen(false);
    setPartnerGroupName('');
    setNumApplications(0);
  };

  return (
    <>
      <Dialog open={partnerDeleteModalOpen} onClose={handleClose}>
        <DialogTitleWithClose onClose={handleClose}>Delete Partner</DialogTitleWithClose>
        <DialogContent>
          <Box>
            <Typography variant='overline2'>Partner Id</Typography>
            <Typography color='textSecondary'>{modalPartner?.id}</Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Partner Name</Typography>
            <Typography color='textSecondary'>{modalPartner?.partnerName}</Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Partner Group</Typography>
            <Typography color='textSecondary'>
              {partnerAppsLoading ? <Spinner type='circular' size={20} /> : partnerGroupName}
            </Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Current Application(s)</Typography>
            <Typography color='textSecondary'>
              {partnerAppsLoading ? (
                <Spinner type='circular' size={20} />
              ) : (
                modalPartnerApps.map((a: string, index: number) => {
                  return <div key={`current-${index}-${a ?? 'missing'}`}>{a}</div>;
                })
              )}
            </Typography>
          </Box>

          <Box>
            <Typography variant='overline2'>Historical Application(s)</Typography>
            <Typography color='textSecondary'>
              {partnerAppsLoading ? (
                <Spinner type='circular' size={20} />
              ) : (
                modalPartnerHistoricApps.map((a: string, index: number) => {
                  return <div key={`historic-${index}-${a ?? 'missing'}`}>{a}</div>;
                })
              )}
            </Typography>
          </Box>
          {numApplications > 0 ? (
            <Alert severity='warning'>
              Partner has current or historic applications. To remove the partner you must delete
              the existing applications first.
            </Alert>
          ) : (
            showWarning && (
              <Alert severity='warning'>
                Are you sure you want to delete this partner? This will delete the partner and
                associated data. You cannot undo this action.
              </Alert>
            )
          )}
        </DialogContent>
        <DialogActions>
          <>
            <Button variant='secondary' size='large' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant='primary'
              size='large'
              onClick={handleOpenConfirmModal}
              disabled={postingPartner || !modalPartner?.canDelete}
            >
              {postingPartner ? (
                <>
                  <span>Deleting ... </span>
                  <Spinner type='circular' size={16} />
                </>
              ) : (
                'Delete Partner'
              )}
            </Button>
          </>

          <ConfirmationModal
            open={confirmDeleteOpen}
            onClose={closeDeleteConfirmation}
            header={'Delete Partner'}
            confirmationText={
              'Are you sure you want to delete this partner? This action cannot be undone.'
            }
            confirmButtonText={'Delete Partner'}
            onDelete={postPartnerDelete}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
